import { UserRegisterData } from "./../../types/user-register-data.interface";
import { redirect } from "@/utils/redirect";
import { computed, readonly, ref, watch } from "vue";
import { api } from "@/api";
import { useToast } from "@/utils/use-toast";
import {
  User,
  UserLoginData,
  UserRecoverPasswordData,
  UserRemindData,
  ToggleName,
} from "@/types";

const { success } = useToast();

const isLoginModalOpen = ref(false);
const isBusy = ref(false);
const user = ref<User | null>((window.authUser as User) ?? null);
const csrfToken = ref(
  document.head.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')!
    .content
);

const toggles = ref<ToggleName[]>([]);

const openLoginModal = (value = true): Promise<boolean> => {
  isLoginModalOpen.value = value;

  if (value) {
    return new Promise((resolve) => {
      const unwatch = watch(isLoginModalOpen, () => {
        unwatch();
        resolve(isLoggedIn.value);
      });
    });
  }

  return Promise.resolve(false);
};

const loginSocial = async (): Promise<void> => {
  isBusy.value = true;
  try {
    const { _token, ...userData } = await api.user.getCurrentUser();
    csrfToken.value = _token;
    user.value = userData;
    isLoginModalOpen.value = false;
  } finally {
    isBusy.value = false;
  }
};

const login = async (data: UserLoginData): Promise<void> => {
  isBusy.value = true;
  try {
    const { _token, ...userData } = await api.user.login(data);
    csrfToken.value = _token;
    user.value = userData;
    isLoginModalOpen.value = false;
  } finally {
    isBusy.value = false;
  }
};

const logout = async (): Promise<void> => {
  isBusy.value = true;
  try {
    await api.user.logout();
    redirect("/");
  } catch {
    isBusy.value = false;
  }
};

const register = async (data: UserRegisterData): Promise<void> => {
  isBusy.value = true;
  try {
    const { _token, ...userData } = await api.user.register(data);
    csrfToken.value = _token;
    user.value = userData;
    isLoginModalOpen.value = false;
    success("user.registerSuccess");
  } finally {
    isBusy.value = false;
  }
};

const remind = async (data: UserRemindData): Promise<void> => {
  isBusy.value = true;
  try {
    await api.user.remind(data);
    isLoginModalOpen.value = false;
    success("user.remindSuccess");
  } finally {
    isBusy.value = false;
  }
};

const recoverPassword = async (
  data: UserRecoverPasswordData
): Promise<void> => {
  isBusy.value = true;
  try {
    await api.user.recoverPassword(data);
    success("user.recoverSuccess");
  } finally {
    isBusy.value = false;
  }
};

const updateUser = (data: Partial<User>): void => {
  if (user.value) {
    user.value = { ...user.value, ...data };
  }
};

const loadToggles = async (): Promise<void> => {
  toggles.value = await api.user.getFeatureToggles();
};

const isLoginModalOpenReadonly = readonly(isLoginModalOpen);
const isBusyReadonly = readonly(isBusy);
const userReadonly = readonly(user);
const csrfTokenReadonly = readonly(csrfToken);
const isLoggedIn = computed(() => !!user.value);

export {
  isLoginModalOpenReadonly as isLoginModalOpen,
  isBusyReadonly as isBusy,
  userReadonly as user,
  csrfTokenReadonly as csrfToken,
  isLoggedIn,
  openLoginModal,
  login,
  register,
  logout,
  remind,
  updateUser,
  recoverPassword,
  loginSocial,
  toggles,
  loadToggles,
};
